// Edits as the sync layer receives them (WEB_PLAN.md §0 "Edits are patches", §2.1, §3.6). domain/ functions return
// lists of these; `SyncEngine.save` turns each into an outbox entry. The web never round-trips an item through a
// typed model: an update is applied to a structuredClone of the latest local payload (the pending outbox payload
// when there is one, else the server payload), so keys this build does not know survive untouched.

import type { Collection } from '@/contracts/collections';
import type { JsonObject } from '@/contracts/wire';

export type Patch =
  | {
      kind: 'create';
      collection: Collection;
      /** Upper-case id (newId()); equals `payload.id` except for `settings`. */
      id: string;
      /** Every key, built by the contracts `newX()` factory. */
      payload: JsonObject;
      /** attachments/documents: `<company lowercase>/<collection>/<payload.fileName>`, uploaded before the save. */
      filePath?: string;
    }
  | {
      kind: 'update';
      collection: Collection;
      id: string;
      /**
       * Mutates the draft in place: set only the keys the user edited; `delete draft.key` clears an optional (never
       * write null). Must be synchronous and must not keep a reference to the draft.
       */
      apply: (draft: JsonObject) => void;
    }
  | { kind: 'delete'; collection: Collection; id: string };

/** Convenience constructors (plain objects work just as well). */
export const patch = {
  create(collection: Collection, id: string, payload: JsonObject, filePath?: string): Patch {
    return filePath === undefined ? { kind: 'create', collection, id, payload } : { kind: 'create', collection, id, payload, filePath };
  },
  update(collection: Collection, id: string, apply: (draft: JsonObject) => void): Patch {
    return { kind: 'update', collection, id, apply };
  },
  /** Sets top-level keys; `undefined` removes the key (a nil optional). */
  set(collection: Collection, id: string, values: Record<string, JsonObject[string] | undefined>): Patch {
    return {
      kind: 'update',
      collection,
      id,
      apply: (draft) => {
        for (const [key, value] of Object.entries(values)) {
          if (value === undefined) delete draft[key];
          else draft[key] = value;
        }
      },
    };
  },
  delete(collection: Collection, id: string): Patch {
    return { kind: 'delete', collection, id };
  },
};
